export const FS_META_PIXEL_ID = "2088352118670061";
export const FS_GA4_ID = "G-FY7WXGYPQW";
export const FS_POSTHOG_KEY =
  "phc_nFJsAdPZ6fD84pDXZxGZYQxZzZatvUejbqmMkaRkhYdL";

const PURCHASE_DEDUPE_PREFIX = "fs_checkout_purchase_fired_";

export const getTrackingSearchParams = (search = window.location.search) =>
  new URLSearchParams(search);

export const getTrackingProduct = (qs = getTrackingSearchParams()) =>
  qs.get("product") || qs.get("med_pref") || "glp1";

export const getDefaultTrackingValue = (product) =>
  product.includes("tirz") ? 149 : 99;

/** Merge updates into the current query string; keeps funnel params (utm_*, lead_id, etc.). */
export const mergeCheckoutSearch = (currentSearch, updates = {}) => {
  const raw =
    typeof currentSearch === "string"
      ? currentSearch.replace(/^\?/, "")
      : currentSearch.toString();
  const sp = new URLSearchParams(raw);

  for (const [key, value] of Object.entries(updates)) {
    if (value != null && String(value).trim() !== "") {
      sp.set(key, String(value).trim());
    }
  }

  return sp.toString();
};

/**
 * Resolves purchase value: explicit total → window.orderTotal → URL value → product fallback.
 */
export const resolvePurchaseAmount = ({
  productTotal,
  search = window.location.search,
} = {}) => {
  if (
    productTotal !== undefined &&
    productTotal !== null &&
    productTotal !== "" &&
    Number.isFinite(Number(productTotal)) &&
    Number(productTotal) >= 0
  ) {
    return Number(productTotal);
  }

  const orderTotal = window.orderTotal;
  if (orderTotal != null && Number.isFinite(orderTotal) && orderTotal >= 0) {
    return orderTotal;
  }

  const qs = getTrackingSearchParams(search);
  const valueFromUrl = Number(qs.get("value"));
  if (Number.isFinite(valueFromUrl) && valueFromUrl >= 0) {
    return valueFromUrl;
  }

  return getDefaultTrackingValue(getTrackingProduct(qs));
};

/**
 * Calls hosted checkout-analytics purchase() after payment (PostHog, GA4, Pixel, Meta CAPI).
 */
export const trackFsPurchase = (amount, currency = "USD", orderId = null) => {
  if (!Number.isFinite(amount) || amount < 0) return;

  const id =
    orderId ||
    getTrackingSearchParams().get("order_id") ||
    getTrackingSearchParams().get("transaction_id");
  const dedupeKey = `${PURCHASE_DEDUPE_PREFIX}${id || "unknown"}`;
  if (sessionStorage.getItem(dedupeKey) === "1") return;

  const fire = () => {
    const api = window.fsCheckoutAnalytics;
    if (!api?.purchase) return false;
    api.purchase(amount, currency);
    sessionStorage.setItem(dedupeKey, "1");
    return true;
  };

  if (fire()) return;

  let attempts = 0;
  const timer = window.setInterval(() => {
    if (fire() || ++attempts >= 50) window.clearInterval(timer);
  }, 100);
};

/**
 * Preserves all incoming URL params and adds order / attribution fields for thank-you.
 */
export const buildThankYouNavigatePath = ({
  orderId,
  productTotal,
  email,
  phoneNumber,
  firstName,
  lastName,
  product,
  hashFragment = "",
}) => {
  const updates = {};

  const resolvedProduct =
    product || getTrackingProduct(getTrackingSearchParams(window.location.search));
  if (resolvedProduct) updates.product = resolvedProduct;

  if (orderId) {
    updates.order_id = String(orderId);
    updates.transaction_id = String(orderId);
  }

  if (
    productTotal !== undefined &&
    productTotal !== null &&
    productTotal !== ""
  ) {
    updates.value = String(productTotal);
  }

  if (email) updates.email = email;
  if (phoneNumber) updates.phone = phoneNumber;
  if (firstName) updates.first_name = firstName;
  if (lastName) updates.last_name = lastName;

  const qs = mergeCheckoutSearch(window.location.search, updates);
  return `/thankyou-page${qs ? `?${qs}` : ""}${hashFragment || ""}`;
};

/** Returns search string with order_id, transaction_id, and value when missing. */
export const ensureThankYouSearchParams = ({
  search = window.location.search,
  orderId,
  productTotal,
}) => {
  const qs = getTrackingSearchParams(search);
  const amount = resolvePurchaseAmount({ productTotal, search });
  const idStr = orderId != null ? String(orderId).trim() : "";

  const hasOrder =
    qs.get("order_id") || qs.get("transaction_id");
  const hasValue = qs.get("value");

  if (hasOrder && hasValue) {
    return { search: qs.toString(), amount, needsNavigate: false };
  }

  if (!idStr || amount == null) {
    return { search: qs.toString(), amount, needsNavigate: false };
  }

  return {
    search: mergeCheckoutSearch(search, {
      order_id: idStr,
      transaction_id: idStr,
      value: String(amount),
    }),
    amount,
    needsNavigate: true,
  };
};
